// ============================================================
// Sentry observability layer for the Square Code shell.
//
// Architecture-aware:
//   app_shell  = square-code      (always)
//   module     = square-shell | olivium | command-center | dashboard | domains
//   mode       = live | demo
//   route      = current pathname
//   panel_mode = detail | comms | action
//
// Privacy-first defaults:
//   - sendDefaultPii: false
//   - replay masks all text + inputs + media
//   - beforeSend / beforeBreadcrumb scrub auth headers, cookies,
//     bearer tokens, supabase access tokens, and obvious secrets
//
// Every helper here is a SAFE NO-OP when Sentry has not been
// initialized (e.g. SSR, tests, or VITE_SENTRY_DSN unset).
// Nothing in this module should ever throw into the React tree.
// ============================================================

import * as Sentry from '@sentry/react'

// ----------------------------------------------------------------
// Module-level state
// ----------------------------------------------------------------

let initialized = false

export type ShellMode = 'live' | 'demo'
export type ModuleId =
  | 'square-shell'
  | 'dashboard'
  | 'command-center'
  | 'olivium'
  | 'projects'
  | 'workspace'
  | 'comms'
  | 'platform-control'

export type PanelMode = 'detail' | 'comms' | 'action'

export type HookName =
  | 'useProjects'
  | 'useProject'
  | 'useWorkspaces'
  | 'useStudioRuns'
  | 'useWorkspaceEvents'
  | 'useLiveRun'
  | 'useSite'
  | 'useSitePages'
  | 'useSiteBlocks'
  | 'useHostingDeployments'
  | 'useHostingDomains'
  | 'useCommandDispatch'
  | 'useAuditFeed'
  | 'useOliviumAlerts'
  | 'useSessions'
  | 'useChainIntegrity'
  | 'usePlatformIntegrations'
  | 'usePlatformWebhooks'
  | 'usePlatformProviderStatus'
  | 'useProjectAccess'

export type HookOutcome =
  | 'live_success'        // query in live mode returned data
  | 'demo_fallback'       // not in live mode by design
  | 'live_error'          // live query returned an error
  | 'missing_dependency'  // table or RPC absent
  | 'realtime_failure'    // postgres_changes subscription threw

export interface HookContext {
  rows?: number
  durationMs?: number
  table?: string
  rpc?: string
  message?: string
  [k: string]: unknown
}

// ----------------------------------------------------------------
// 1. Init
// ----------------------------------------------------------------

export interface InitOptions {
  dsn?: string
  release?: string
  environment?: string
  mode: ShellMode
}

export function initSentry(opts: InitOptions): boolean {
  if (initialized) return true
  if (!opts.dsn) return false
  if (typeof window === 'undefined') return false

  try {
    Sentry.init({
      dsn: opts.dsn,
      release: opts.release,
      environment: opts.environment || 'development',
      sendDefaultPii: false,
      // Performance — modest in prod, easy to dial up later.
      tracesSampleRate: 0.1,
      // Replay — only on errors by default. Sessions sampled lightly.
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          // Network bodies are noisy and risky for legal-adjacent data.
          networkDetailAllowUrls: [],
        }),
        Sentry.feedbackIntegration({
          autoInject: false,        // we trigger from a custom button
          showBranding: false,
          colorScheme: 'dark',
          buttonLabel: 'Report issue',
          formTitle: 'Report a Square Code issue',
          submitButtonLabel: 'Send report',
        }),
      ],
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
    })

    Sentry.setTags({
      app_shell: 'square-code',
      mode: opts.mode,
    })

    initialized = true
    return true
  } catch (err) {
    // Never let Sentry init crash the shell.
    // eslint-disable-next-line no-console
    console.warn('[observability] Sentry init failed', err)
    return false
  }
}

export function isInitialized(): boolean {
  return initialized
}

// ----------------------------------------------------------------
// 2. Tag / context helpers
// ----------------------------------------------------------------

export function setMode(mode: ShellMode): void {
  if (!initialized) return
  Sentry.setTag('mode', mode)
}

export function setRoute(pathname: string): void {
  if (!initialized) return
  Sentry.setTag('route', pathname)
  Sentry.setTag('module', deriveModuleFromRoute(pathname))
}

export function setPanelMode(mode: PanelMode): void {
  if (!initialized) return
  Sentry.setTag('panel_mode', mode)
}

export function setSelectionContext(kind: 'project' | 'event' | 'session', payload: Record<string, unknown> | null): void {
  if (!initialized) return
  if (!payload) {
    Sentry.setContext('selection', null)
    return
  }
  // Tags filter; context describes. Keep it small.
  Sentry.setContext('selection', {
    kind,
    ...sanitizeContext(payload),
  })
}

export function setChainContext(state: { valid: boolean; missingRpc: boolean; checked: number; message: string }): void {
  if (!initialized) return
  Sentry.setContext('chain_integrity', state)
  Sentry.setTag(
    'chain_state',
    state.missingRpc ? 'awaiting_service' : state.valid ? 'valid' : 'invalid'
  )
}

function deriveModuleFromRoute(pathname: string): ModuleId {
  if (pathname.startsWith('/square/olivium')) return 'olivium'
  if (pathname.startsWith('/square/command-center')) return 'command-center'
  if (pathname.startsWith('/square/dashboard')) return 'dashboard'
  if (pathname.startsWith('/square/projects')) return 'projects'
  if (pathname.startsWith('/square/workspace')) return 'workspace'
  if (pathname.startsWith('/square/quamspace')) return 'workspace'
  if (pathname.startsWith('/square/comms')) return 'comms'
  if (pathname.startsWith('/square/control')) return 'platform-control'
  if (pathname.startsWith('/square/admin')) return 'platform-control'
  return 'square-shell'
}

// ----------------------------------------------------------------
// 3. Hook instrumentation helpers
// ----------------------------------------------------------------

export function recordHookOutcome(hook: HookName, outcome: HookOutcome, ctx: HookContext = {}): void {
  if (!initialized) return
  Sentry.addBreadcrumb({
    category: 'hook.outcome',
    level: outcomeLevel(outcome),
    message: `${hook}: ${outcome}`,
    data: sanitizeContext({ hook, outcome, ...ctx }),
  })
}

export function captureHookFailure(
  hook: HookName,
  outcome: HookOutcome,
  err: unknown,
  ctx: HookContext = {}
): void {
  if (!initialized) return
  const error = err instanceof Error ? err : new Error(String(err))
  Sentry.withScope((scope) => {
    scope.setTag('hook', hook)
    scope.setTag('hook_outcome', outcome)
    if (ctx.table) scope.setTag('table', ctx.table)
    if (ctx.rpc) scope.setTag('rpc', ctx.rpc)
    scope.setContext('hook_context', sanitizeContext(ctx))
    scope.setLevel(outcomeLevel(outcome))
    Sentry.captureException(error)
  })
}

/**
 * withSpan — wrap an async operation in a Sentry span. The span
 * always resolves; failures are surfaced through captureHookFailure
 * (so the caller still controls whether the failure is fatal).
 */
export async function withSpan<T>(
  name: string,
  op: 'db.query' | 'db.rpc' | 'db.subscribe' | 'shell.action' | 'shell.transition',
  fn: () => Promise<T>
): Promise<T> {
  if (!initialized) return fn()
  return Sentry.startSpan({ name, op }, async () => fn())
}

// ----------------------------------------------------------------
// 4. Shell-action breadcrumbs
// ----------------------------------------------------------------

export function recordShellAction(action: string, data: Record<string, unknown> = {}): void {
  if (!initialized) return
  Sentry.addBreadcrumb({
    category: 'shell.action',
    level: 'info',
    message: action,
    data: sanitizeContext(data),
  })
}

// ----------------------------------------------------------------
// 5. Feedback dialog
// ----------------------------------------------------------------

export function openFeedbackDialog(): void {
  if (!initialized) return
  try {
    const integration = (Sentry as unknown as {
      getFeedback?: () => { openDialog?: () => void } | null
    }).getFeedback?.()
    integration?.openDialog?.()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[observability] feedback open failed', err)
  }
}

export function isFeedbackAvailable(): boolean {
  if (!initialized) return false
  try {
    return Boolean((Sentry as unknown as { getFeedback?: () => unknown }).getFeedback?.())
  } catch {
    return false
  }
}

// ----------------------------------------------------------------
// 6. React error boundary
// ----------------------------------------------------------------

export const SentryErrorBoundary = Sentry.ErrorBoundary

// ----------------------------------------------------------------
// 7. Privacy scrubbing
// ----------------------------------------------------------------

const FORBIDDEN_KEY_PATTERNS = [
  /authorization/i,
  /cookie/i,
  /supabase[-_]?auth/i,
  /access[-_]?token/i,
  /refresh[-_]?token/i,
  /api[-_]?key/i,
  /^password/i,
  /secret/i,
  /private[-_]?key/i,
]

function isSensitiveKey(key: string): boolean {
  return FORBIDDEN_KEY_PATTERNS.some((rx) => rx.test(key))
}

function scrubObject(obj: unknown, depth = 0): unknown {
  if (depth > 4 || obj === null || obj === undefined) return obj
  if (typeof obj === 'string') {
    return obj.length > 1024 ? obj.slice(0, 1024) + '…' : obj
  }
  if (Array.isArray(obj)) {
    return obj.slice(0, 50).map((v) => scrubObject(v, depth + 1))
  }
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (isSensitiveKey(k)) {
        out[k] = '[scrubbed]'
      } else {
        out[k] = scrubObject(v, depth + 1)
      }
    }
    return out
  }
  return obj
}

function sanitizeContext(ctx: Record<string, unknown>): Record<string, unknown> {
  return scrubObject(ctx) as Record<string, unknown>
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Strip request headers + cookies entirely.
  if (event.request) {
    if (event.request.headers) event.request.headers = scrubHeaders(event.request.headers)
    delete event.request.cookies
  }
  if (event.contexts) {
    event.contexts = scrubObject(event.contexts) as typeof event.contexts
  }
  if (event.extra) {
    event.extra = scrubObject(event.extra) as typeof event.extra
  }
  // Strip URL query strings — they may carry tokens.
  if (event.request?.url && typeof event.request.url === 'string') {
    event.request.url = event.request.url.split('?')[0]
  }
  return event
}

function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) {
    out[k] = isSensitiveKey(k) ? '[scrubbed]' : v
  }
  return out
}

function scrubBreadcrumb(crumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (crumb.data) {
    crumb.data = scrubObject(crumb.data) as typeof crumb.data
  }
  // Drop noisy XHR breadcrumbs that may carry auth headers in detail.
  if (crumb.category === 'xhr' || crumb.category === 'fetch') {
    if (crumb.data) delete (crumb.data as Record<string, unknown>).request_body_size
  }
  return crumb
}

function outcomeLevel(outcome: HookOutcome): Sentry.SeverityLevel {
  switch (outcome) {
    case 'live_success':       return 'info'
    case 'demo_fallback':      return 'info'
    case 'realtime_failure':   return 'warning'
    case 'missing_dependency': return 'warning'
    case 'live_error':         return 'error'
    default:                   return 'info'
  }
}
